import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { PlanState, ProtectionType, TravelType } from './types'
import { BannerType } from '../../components/BannerLabel'
import { TransformedMasterData } from '../../../../types/types'
import { planExtraBuilderFunction } from './extraReducers'

const initialState: PlanState = {
  // state
  isGetAllPlansLoading: false,
  isExportPlansLoading: false,
  isAddPlanLoading: false,
  isDeletePlanLoading: false,
  isDownloadCountryTemplateLoading: false,
  isGetCountryIdListFromExcelLoading: false,
  isExportPlanCountryByIdLoading: false,
  isDownloadPriceTemplateLoading: false,
  isGetPriceListFromExcelLoading: false,
  isExportPlanPriceByIdLoading: false,
  isGetPlanStatusLoading: false,
  isGetPlanInfoLoading: false,
  isGetPlanTypeLoading: false,
  isGetPlanCountriesLoading: false,
  isGetPlanPricesLoading: false,
  isGetPlanSpecialLabelLoading: false,
  isGetPlanDetailLoading: false,
  isGetPlanExceptionLoading: false,
  isGetPlanTimestampLoading: false,
  isUpdatePlanStatusLoading: false,
  isUpdatePlanInfoLoading: false,
  isUpdatePlanTypeLoading: false,
  isUpdatePlanCountriesLoading: false,
  isUpdatePlanPricesLoading: false,
  isUpdateSpecialLabelLoading: false,
  isUpdatePlanDetailLoading: false,
  isUpdatePlanExceptionLoading: false,
  isDownloadPlanTemplateLoading: false,
  isImportPlanTemplateLoading: false,
  isPlanActive: true,
  bannerType: BannerType.None,
  travelType: TravelType.Trip,
  protectionType: ProtectionType.Domestic,
  filteredRegions: [],
  selectedPlanCountries: [],
  currentAbbreviationName: '',
  allCountriesList: [],
  prices: [],
  yearPrice: '0',
  planFile: null,
  tax: '0',
  insurancePrice: '0',

  // fetched
  planCountries: [],
  planException: ''
}

const slice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    setCurrentAbbreviationName: (state, action: PayloadAction<string>) => {
      state.currentAbbreviationName = action.payload
    },
    changeProtectionType: (state, action: PayloadAction<ProtectionType>) => {
      state.protectionType = action.payload
    },
    changeTravelType: (state, action: PayloadAction<TravelType>) => {
      state.travelType = action.payload
    },
    changeFilteredRegion: (state, action: PayloadAction<string>) => {
      const regionId = action.payload
      if (state.filteredRegions.includes(regionId)) {
        const findIndex = state.filteredRegions.findIndex((value) => value === regionId)
        if (findIndex > -1) {
          state.filteredRegions.splice(findIndex, 1)
        }
      } else {
        state.filteredRegions.push(regionId)
      }
    },
    setFilteredRegion: (state, action: PayloadAction<string[]>) => {
      state.filteredRegions = action.payload
    },
    toggleCountry: (state, action: PayloadAction<string>) => {
      const countryId = action.payload
      if (state.selectedPlanCountries.includes(countryId)) {
        const findIndex = state.selectedPlanCountries.findIndex((value) => value === countryId)
        if (findIndex > -1) {
          state.selectedPlanCountries.splice(findIndex, 1)
        }
      } else {
        state.selectedPlanCountries.push(countryId)
      }
    },
    selectAllCountries: (state, action: PayloadAction<string[]>) => {
      state.selectedPlanCountries = []
      state.selectedPlanCountries.push(...action.payload)
    },
    togglePlanActive: (state) => {
      state.isPlanActive = !state.isPlanActive
    },
    changeBannerType: (state, action: PayloadAction<BannerType>) => {
      if (state.bannerType === action.payload) {
        state.bannerType = BannerType.None
      } else {
        state.bannerType = action.payload
      }
    },
    setAllCountriesList: (state, action: PayloadAction<TransformedMasterData[]>) => {
      state.allCountriesList = action.payload
    },
    setYearPrice: (state, action: PayloadAction<string>) => {
      state.yearPrice = action.payload
    },
    setPlanFile: (state, action: PayloadAction<File | null>) => {
      state.planFile = action.payload
    },
    clearPrices: (state) => {
      state.prices = []
    },
    setTaxPrice: (state, action: PayloadAction<string>) => {
      state.tax = action.payload
    },
    setInsurancePricePrice: (state, action: PayloadAction<string>) => {
      state.insurancePrice = action.payload
    },
    resetPlanState: () => initialState
  },
  extraReducers: planExtraBuilderFunction
})

export const {
  setCurrentAbbreviationName,
  resetPlanState,
  togglePlanActive,
  changeBannerType,
  changeTravelType,
  changeProtectionType,
  changeFilteredRegion,
  toggleCountry,
  selectAllCountries,
  setFilteredRegion,
  setAllCountriesList,
  setYearPrice,
  setPlanFile,
  clearPrices,
  setTaxPrice,
  setInsurancePricePrice
} = slice.actions
export default slice.reducer
